/**
 * 模板区块识别：将模板网格按空行空列切分为语义区块（zone/side）。
 *
 * 定位：提取阶段已获得 textGrid（完整文本网格）、merges（合并信息）、dataRegions（表格区域）；
 * 本模块从这些信息推导区块的纵向语义（header/body/detail/footer）与横向位置（left/center/right），
 * 为后续无标签数据位定位与知识库打分提供上下文。
 *
 * 算法概要：
 *   1. 空行空列分析：找出所有完全空白的行/列 → 分隔符
 *   2. 水平带划分：相邻非空行聚合为一个水平带（HBand）
 *   3. 垂直带划分：相邻非空列聚合为一个垂直带（VBand）
 *   4. 组合：水平带×垂直带 → 区块（Block）
 *   5. 归一化位置：y = rowStart/rowCount（0..1）→ zone；x = colStart/colCount（0..1）→ side
 *   6. 数据区标记：检查是否与 dataRegions 相交 → zone=detail
 */

export type Zone = 'header' | 'body' | 'detail' | 'footer';
export type Side = 'left' | 'center' | 'right';

export interface TemplateBlock {
  id: number;
  rowStart: number;
  rowEnd: number;
  colStart: number;
  colEnd: number;
  zone: Zone;
  side: Side;
}

export interface SegmentGridInput {
  maxRow: number;
  maxCol: number;
  textGrid: string[][];
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>;
  dataRegions: Array<{ headerRow: number; headerCol: number; rowCount: number }>;
}

interface HorizontalBand {
  rowStart: number;
  rowEnd: number;
}

interface VerticalBand {
  colStart: number;
  colEnd: number;
}

/**
 * 检查行是否完全空白（无文本、无合并跨入）
 */
function isEmptyRow(
  row: number,
  maxCol: number,
  textGrid: string[][],
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>,
): boolean {
  for (let c = 1; c <= maxCol; c++) {
    if (textGrid[row]?.[c]) return false;
  }
  for (const m of merges) {
    if (m.row < row && m.row + m.rowspan > row) return false;
  }
  return true;
}

/**
 * 检查列是否完全空白
 */
function isEmptyCol(
  col: number,
  maxRow: number,
  textGrid: string[][],
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>,
): boolean {
  for (let r = 1; r <= maxRow; r++) {
    if (textGrid[r]?.[col]) return false;
  }
  for (const m of merges) {
    if (m.col < col && m.col + m.colspan > col) return false;
  }
  return true;
}

/**
 * 识别水平带（相邻非空行）
 */
function findHorizontalBands(
  maxRow: number,
  maxCol: number,
  textGrid: string[][],
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>,
): HorizontalBand[] {
  const bands: HorizontalBand[] = [];
  let inBand = false;
  let bandStart = 0;

  for (let r = 1; r <= maxRow; r++) {
    const empty = isEmptyRow(r, maxCol, textGrid, merges);
    if (!empty) {
      if (!inBand) {
        bandStart = r;
        inBand = true;
      }
    } else {
      if (inBand) {
        bands.push({ rowStart: bandStart, rowEnd: r - 1 });
        inBand = false;
      }
    }
  }
  if (inBand) {
    bands.push({ rowStart: bandStart, rowEnd: maxRow });
  }

  return bands;
}

/**
 * 识别垂直带（相邻非空列）
 */
function findVerticalBands(
  maxRow: number,
  maxCol: number,
  textGrid: string[][],
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>,
): VerticalBand[] {
  const bands: VerticalBand[] = [];
  let inBand = false;
  let bandStart = 0;

  for (let c = 1; c <= maxCol; c++) {
    const empty = isEmptyCol(c, maxRow, textGrid, merges);
    if (!empty) {
      if (!inBand) {
        bandStart = c;
        inBand = true;
      }
    } else {
      if (inBand) {
        bands.push({ colStart: bandStart, colEnd: c - 1 });
        inBand = false;
      }
    }
  }
  if (inBand) {
    bands.push({ colStart: bandStart, colEnd: maxCol });
  }

  return bands;
}

/**
 * 从纵向位置推导 zone
 * - 0.0 ~ 0.15：header（一般表题/表头）
 * - 0.15 ~ 0.5：body（通常是汇总/小计）
 * - 0.5 ~ 0.9：detail（数据明细区）
 * - 0.9 ~ 1.0：footer（页脚/合计）
 */
function inferZone(
  bandStart: number,
  bandEnd: number,
  maxRow: number,
  dataRegions: Array<{ headerRow: number; headerCol: number; rowCount: number }>,
): Zone {
  const midRow = (bandStart + bandEnd) / 2;
  const normalizedPos = midRow / maxRow;

  // 检查是否与 dataRegion 明细区重叠
  for (const dr of dataRegions) {
    const detailRowStart = dr.headerRow + 1;
    const detailRowEnd = dr.headerRow + dr.rowCount;
    if (midRow >= detailRowStart && midRow <= detailRowEnd) {
      return 'detail';
    }
  }

  if (normalizedPos < 0.15) return 'header';
  if (normalizedPos < 0.5) return 'body';
  if (normalizedPos < 0.9) return 'detail';
  return 'footer';
}

/**
 * 从横向位置推导 side
 * - 0.0 ~ 0.33：left
 * - 0.33 ~ 0.67：center
 * - 0.67 ~ 1.0：right
 */
function inferSide(colStart: number, colEnd: number, maxCol: number): Side {
  const midCol = (colStart + colEnd) / 2;
  const normalizedPos = midCol / maxCol;

  if (normalizedPos < 0.33) return 'left';
  if (normalizedPos < 0.67) return 'center';
  return 'right';
}

/**
 * 主函数：对模板网格进行区块分割
 */
export function segmentBlocks(input: SegmentGridInput): TemplateBlock[] {
  const { maxRow, maxCol, textGrid, merges, dataRegions } = input;

  const hBands = findHorizontalBands(maxRow, maxCol, textGrid, merges);
  const vBands = findVerticalBands(maxRow, maxCol, textGrid, merges);

  const blocks: TemplateBlock[] = [];
  let blockId = 1;

  for (const hBand of hBands) {
    for (const vBand of vBands) {
      const zone = inferZone(hBand.rowStart, hBand.rowEnd, maxRow, dataRegions);
      const side = inferSide(vBand.colStart, vBand.colEnd, maxCol);

      blocks.push({
        id: blockId++,
        rowStart: hBand.rowStart,
        rowEnd: hBand.rowEnd,
        colStart: vBand.colStart,
        colEnd: vBand.colEnd,
        zone,
        side,
      });
    }
  }

  return blocks;
}
